/**
 * LinkedIn Posts API `commentary` is not plain text — it is "little" text.
 *
 * learn.microsoft.com/linkedin/marketing/community-management/shares/little-text-format
 * (li-lms-2026-09): "All reserved characters need to be escaped with a
 * backslash, even if those characters are not used in one of the supported
 * elements or templates." Unescaped, a caption such as "results (up 12%)" or
 * "data_driven" is parsed as markup and can publish altered or truncated,
 * while LinkedIn still answers 201 — a false success on the published post.
 *
 * Hashtags are preserved deliberately: every `#word` becomes the documented
 * HashtagTemplate `{hashtag|\#|word}`, so escaping never turns a real hashtag
 * into literal text. `toLinkedInCommentary` never emits mention syntax, so `@` is always text there;
 * `toLinkedInCommentaryWithMentions` adds organic company-page tags.
 */

export const LINKEDIN_COMMENTARY_MAX_CHARS = 3000;

const RESERVED = /[\\|{}@[\]()<>#*_~]/g;
const HASHTAG = /(^|[^\p{L}\p{N}_&])#([\p{L}\p{N}_]+)/gu;

function escapeReserved(text: string): string {
  return text.replace(RESERVED, (char) => `\\${char}`);
}

export function toLinkedInCommentary(text: string): string {
  let result = '';
  let cursor = 0;
  for (const match of text.matchAll(HASHTAG)) {
    const [whole, prefix, tag] = match;
    const start = match.index ?? 0;
    result += escapeReserved(text.slice(cursor, start) + prefix);
    result += `{hashtag|\\#|${escapeReserved(tag)}}`;
    cursor = start + whole.length;
  }
  return result + escapeReserved(text.slice(cursor));
}

export interface LinkedInMention {
  urn: string;
  name: string;
  vanityName?: string | null;
  logoUrl?: string | null;
}

export interface LinkedInMentionSkip {
  name: string;
  reason: 'invalid_urn' | 'not_in_text';
}

export interface LinkedInCommentaryWithMentions {
  commentary: string;
  applied: LinkedInMention[];
  skipped: LinkedInMentionSkip[];
}

const ORGANIZATION_URN = /^urn:li:organization:\d+$/;

export function isLinkedInOrganizationUrn(value: unknown): value is string {
  return typeof value === 'string' && ORGANIZATION_URN.test(value);
}

function segmentCommentary(segment: string, precedingChar: string | null): string {
  if (precedingChar === null) return toLinkedInCommentary(segment);
  const sentinel = /[\p{L}\p{N}_&]/u.test(precedingChar) ? 'a' : ' ';
  return toLinkedInCommentary(sentinel + segment).slice(1);
}

/**
 * Organic company-page tags. The first exact, case-sensitive occurrence of each
 * mention's name becomes `@[name](urn)`; everything else goes through
 * `toLinkedInCommentary`. With no usable mentions the output is byte-identical
 * to `toLinkedInCommentary(text)`.
 */
export function toLinkedInCommentaryWithMentions(
  text: string,
  mentions?: readonly LinkedInMention[] | null,
): LinkedInCommentaryWithMentions {
  const skipped: LinkedInMentionSkip[] = [];
  const placed: Array<{ start: number; end: number; mention: LinkedInMention }> = [];

  for (const mention of mentions ?? []) {
    const name = typeof mention?.name === 'string' ? mention.name : '';
    if (!isLinkedInOrganizationUrn(mention?.urn)) {
      skipped.push({ name, reason: 'invalid_urn' });
      continue;
    }
    if (!name) {
      skipped.push({ name, reason: 'not_in_text' });
      continue;
    }
    let from = 0;
    let found = -1;
    while (from <= text.length) {
      const index = text.indexOf(name, from);
      if (index === -1) break;
      const end = index + name.length;
      if (!placed.some((p) => index < p.end && end > p.start)) {
        found = index;
        break;
      }
      from = index + 1;
    }
    if (found === -1) {
      skipped.push({ name, reason: 'not_in_text' });
      continue;
    }
    placed.push({ start: found, end: found + name.length, mention });
  }

  if (placed.length === 0) {
    return { commentary: toLinkedInCommentary(text), applied: [], skipped };
  }

  placed.sort((a, b) => a.start - b.start);
  let result = '';
  let cursor = 0;
  let previous: string | null = null;
  for (const { start, end, mention } of placed) {
    result += segmentCommentary(text.slice(cursor, start), previous);
    result += `@[${escapeReserved(mention.name)}](${mention.urn})`;
    previous = mention.name.slice(-1);
    cursor = end;
  }
  result += segmentCommentary(text.slice(cursor), previous);
  return { commentary: result, applied: placed.map((p) => p.mention), skipped };
}

const HASHTAG_TEMPLATE = /\{hashtag\|\\?[#＃]\|((?:\\.|[^}])*)\}/g;
const MENTION = /@\[((?:\\.|[^\]])*)\]\((urn:li:[^)]+)\)/g;
const ESCAPED = /\\([\\|{}@[\]()<>#*_~])/g;

/**
 * The reverse, for text READ from LinkedIn: the Posts API returns commentary
 * in the same format ("Follow best practices {hashtag|\\#|coding}"), so a
 * reader that keeps it raw stores templates and backslashes as if the author
 * wrote them. Mentions keep their display name; hashtags keep their sign.
 */
export function fromLinkedInCommentary(commentary: string): string {
  return commentary
    .replace(HASHTAG_TEMPLATE, (_whole, tag: string) => `#${tag}`)
    .replace(MENTION, (_whole, name: string) => name)
    .replace(ESCAPED, '$1');
}

/** Past this many characters of the 3,000 the editor starts saying how close the caption is. */
export const LINKEDIN_COMMENTARY_WARN_CHARS = 2850;

export type LinkedInCaptionLengthLevel = 'ok' | 'near' | 'escaped_over' | 'over';

export interface LinkedInCaptionLength {
  /** Characters the person typed. */
  raw: number;
  /** Characters sent to LinkedIn after escaping reserved characters and hashtag templates. */
  escaped: number;
  level: LinkedInCaptionLengthLevel;
}

/**
 * LinkedIn's Posts API documents no numeric cap, only a 400 FIELD_LENGTH_TOO_LONG, and does not say whether the cap
 * counts the escaped text it receives (`\(` is two characters, a hashtag becomes `{hashtag|\#|word}`). 3,000 is the
 * app's limit on what a person types. So the typed count stays the hard limit, and a caption that is within it but
 * longer than 3,000 once escaped is flagged, because it is the one LinkedIn might refuse. Live check: see the run note.
 */
export function linkedInCaptionLength(text: string): LinkedInCaptionLength {
  const raw = text.length;
  const escaped = toLinkedInCommentary(text).length;
  const level: LinkedInCaptionLengthLevel =
    raw > LINKEDIN_COMMENTARY_MAX_CHARS
      ? 'over'
      : escaped > LINKEDIN_COMMENTARY_MAX_CHARS
        ? 'escaped_over'
        : raw >= LINKEDIN_COMMENTARY_WARN_CHARS
          ? 'near'
          : 'ok';
  return { raw, escaped, level };
}
